import * as v from 'valibot'
import client, { getErrorMessage } from '@client-shared/utils/client-rest'
import { getReportProgressSchema } from '@shared-schemas/reports'
import { handleAsync } from '@client-shared/utils/async'

const report_progress_schema = getReportProgressSchema({ expect_short_uuid: true })

type ReportProgress = v.InferOutput<typeof report_progress_schema>
export type ReportProgressHandler = (results: ReportProgress[]) => void

// TODO: how do we determine when in-progress items turn into completed items? The endpoint should only return queued or in-progress status types
// TODO: with subsequent requests, send the previously reported IDs as a param so that the API can diff the statuses and tell us if previous ones have completed

class ReportsProgressEmitter {
	#last_results: ReportProgress[] | null = null
	#handlers: ReportProgressHandler[] = []
	#customer_id = ''
	#timer: NodeJS.Timeout | null = null
	#debug = false

	constructor(params: { debug?: boolean } = {}) {
		const { debug = false } = params

		this.#debug = debug
	}

	subscribe(handler: ReportProgressHandler) {
		if (this.#handlers.includes(handler)) {
			return
		}

		this.#handlers.push(handler)
	}

	unsubscribe(remove_handler: ReportProgressHandler) {
		this.#handlers = this.#handlers.filter((handler) => handler !== remove_handler)

		if (!this.#handlers.length) {
			this.#stopPolling()
		}
	}

	setCustomerId(customer_id: string) {
		if (this.#customer_id === customer_id) {
			return
		}

		this.#customer_id = customer_id
		this.#last_results = []

		this.#debugLog('set customer id, performing fetch', customer_id)

		this.startPolling()
	}

	startPolling() {
		this.#debugLog('start polling')

		if (this.#timer) {
			clearInterval(this.#timer)
		}

		this.#fetchResults({ initial_fetch: true }).then((res) => {
			this.#timer = setInterval(() => {
				this.#fetchResults()
			}, 60000) // Note: TomTom worker polling interval is setup as an exponential backoff starting at 30s up to 960s (30s => 60s, ..., 960s)
		})
	}

	#stopPolling() {
		if (!this.#timer) {
			return
		}

		clearInterval(this.#timer)
	}

	get is_polling() {
		return this.#timer !== null
	}

	get running_reports_count() {
		return Array.isArray(this.#last_results)
			? this.#last_results.filter((result) => result.status === 'queued' || result.status === 'in_progress').length
			: 0
	}

	get cumulative_progress() {
		if (!Array.isArray(this.#last_results)) {
			return 0
		}

		const in_progress_results = this.#last_results.filter((result) => result.status === 'in_progress')
		const total_progress = in_progress_results.reduce((acum, result) => acum + (result.progress ?? 0), 0)

		return total_progress > 0 ? Math.round((total_progress / (100 * in_progress_results.length)) * 100) : 0
	}

	#emitLastResults() {
		if (!Array.isArray(this.#last_results)) {
			return
		}

		for (const handler of this.#handlers) {
			handler(this.#last_results)
		}
	}

	removeCurrentResult(report_id: string) {
		if (!Array.isArray(this.#last_results)) {
			return
		}

		this.#last_results = this.#last_results.filter((item) => item.id !== report_id)

		this.#emitLastResults()
	}

	async #fetchResults(params: { initial_fetch?: boolean } = {}) {
		const { initial_fetch = false } = params

		this.#debugLog('fetching reports progress', { initial_fetch })

		if (!this.#customer_id) {
			this.#debugLog('skipping check; no customer set')
			return
		}

		if (!this.#handlers.length && !initial_fetch) {
			this.#debugLog('skipping check; no registered handlers')
			return
		}

		const query_params = new URLSearchParams()
		query_params.set('customer_id', this.#customer_id)
		query_params.set(
			'check_report_ids',
			this.#last_results
				?.filter((result) => result.status === 'queued' || result.status === 'in_progress')
				.map((result) => result.id)
				.join(',') ?? '',
		)

		const [fetch_res, fetch_err] = await handleAsync(
			client(`/api/v1/pages/reports/progress?${query_params.toString()}`),
		)

		if (fetch_err) {
			const err_msg = getErrorMessage(fetch_err, 'An unknown error occurred')

			this.#debugLog('error occurred during fetch', err_msg, fetch_err)

			return
		}

		const validated_res = v.safeParse(v.array(report_progress_schema), fetch_res)

		if (!validated_res.success) {
			this.#debugLog('invalid fetch response', validated_res.issues)

			return
		}

		this.#last_results = validated_res.output

		this.#debugLog('reports progress response', this.#last_results)

		if (!this.#last_results.length) {
			// cancel polling since all reports are completed
			this.#debugLog('all reports complete; cancelling status polling timer')
			this.#stopPolling()
		}

		this.#emitLastResults()

		return this.#last_results
	}

	#debugLog(...args: unknown[]) {
		if (!this.#debug) {
			return
		}

		console.log('ReportsProgressEmitter', ...args)
	}
}

export default new ReportsProgressEmitter()
